// Review formatting, sorting and the keyword theme pick shared by the Reviews page sections.
import { cap, clean, num, stripHtml } from './quinn';
import type { Review } from './site';

/** "2026-07-14" → "July 2026"; '' when the date does not parse. */
export function reviewMonth(v: unknown): string {
  const raw = clean(v);
  const d = raw ? new Date(raw) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '';
}

/** "willie tisdale" → "Willie T."; a one-word name prints as-is. */
export function reviewerName(v: unknown): string {
  const parts = (clean(v) || '').split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || '';
  const first = parts[0][0].toUpperCase() + parts[0].slice(1).toLowerCase();
  return `${first} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

/** google_places → "Google"; any other source prints as its own label. */
export function sourceLabel(v: unknown): string {
  const s = clean(v);
  if (!s) return '';
  if (/^google/i.test(s)) return 'Google';
  return cap(s.replace(/[_-]+/g, ' '));
}

export const reviewRating = (r: Review) => num(r.rating) ?? 0;

/** Mentions the leasing team: a role word, or a title before a name ("Ms. Debra"). */
export function mentionsTeam(r: Review): boolean {
  const t = stripHtml(r.text);
  return /\b(team|staff|agents?|office|leasing|managers?|management|maintenance)\b/i.test(t) || /\b(Ms|Mr|Mrs|Miss)\.?\s+[A-Z]/.test(t);
}

export type ReviewSort = 'newest' | 'rated' | 'team';

const byDate = (a: Review, b: Review) => String(b.date || '').localeCompare(String(a.date || ''));

/** The qualifying reviews in the chosen order; "team" keeps only reviews that mention the team. */
export function sortReviews(list: Review[], sort: ReviewSort): Review[] {
  if (sort === 'rated') return list.slice().sort((a, b) => reviewRating(b) - reviewRating(a) || byDate(a, b));
  if (sort === 'team') return list.filter(mentionsTeam).sort(byDate);
  return list.slice().sort(byDate);
}

export interface ReviewTheme {
  label: string;
  signal: string;
}

const THEMES: Array<[string, RegExp]> = [
  ['Leasing team', /\b(leasing|staff|team|agents?|office|managers?|management|customer service)\b/i],
  ['Tours', /\btour(s|ed|ing)?\b/i],
  ['The homes', /\b(homes?|houses?|layouts?|floor ?plans?|spacious|kitchens?|bedrooms?)\b/i],
  ['Community', /\b(community|neighbou?rs)\b/i],
  ['Maintenance', /\b(maintenance|maintained|repair(ed|s)?|fix(ed)?|work orders?)\b/i],
  ['Communication', /\b(communicat\w*|responsive|respond\w*|calls? back|emails?)\b/i],
  ['Amenities', /\b(amenit\w*|pool|gym|fitness|clubhouse|dog park|playground)\b/i],
  ['Location', /\b(location|located|close to|minutes from|convenient)\b/i],
  ['Quiet', /\b(quiet|peaceful|calm)\b/i],
  ['Parking', /\b(parking|garages?)\b/i],
  ['Fees', /\b(fees?|rent|pricing|price|deposit|afford\w*)\b/i],
  ['Screening', /\b(screening|background check|credit check|application process|approv(al|ed))\b/i],
];

const SENTENCE = /(?<=[.!?])(?<!\b(?:Mr|Ms|Mrs|Dr|St)\.)\s+/;
const CLAUSE = /[,;:]\s+|\s+(?:and|but)\s+/;
const words = (t: string) => t.split(/\s+/).filter(Boolean);
const tidy = (t: string) => t.trim().replace(/^(and|but|so|or)\s+/i, '').replace(/[\s,;:.!?…]+$/, '');

/** Eight words around the keyword, from its sentence. */
function around(text: string, rx: RegExp): string {
  const s = text.split(SENTENCE).find((x) => rx.test(x));
  if (!s) return '';
  const w = words(s);
  const at = Math.max(0, w.findIndex((x) => rx.test(x)));
  const start = w.length <= 8 ? 0 : Math.max(0, Math.min(at - 3, w.length - 8));
  return tidy(w.slice(start, start + 8).join(' '));
}

/**
 * A quote for a theme: the first whole clause of two to eight words that raises it, newest review first,
 * not already used by another chip; else eight words around the keyword in the newest review.
 */
function signalFor(texts: string[], hits: number[], rx: RegExp, used: Set<string>): string {
  for (const i of hits)
    for (const s of texts[i].split(SENTENCE))
      for (const c of s.split(CLAUSE)) {
        const frag = tidy(c);
        const n = words(frag).length;
        if (n >= 2 && n <= 8 && rx.test(frag) && !used.has(frag)) return frag;
      }
  return around(texts[hits[0]], rx);
}

/**
 * The themes at least two reviews raise, most-raised first (ties: the newest mention), up to six.
 * `reviews` must be newest first.
 */
export function keywordThemes(reviews: Review[]): ReviewTheme[] {
  const texts = reviews.map((r) => stripHtml(r.text));
  const used = new Set<string>();
  return THEMES.map(([label, rx]) => ({ label, rx, hits: texts.map((t, i) => (rx.test(t) ? i : -1)).filter((i) => i >= 0) }))
    .filter((x) => x.hits.length >= 2)
    .sort((a, b) => b.hits.length - a.hits.length || a.hits[0] - b.hits[0])
    .slice(0, 6)
    .map((x) => {
      const signal = signalFor(texts, x.hits, x.rx, used);
      used.add(signal);
      return { label: x.label, signal };
    });
}

/** A signal as shown: one pair of curly quotes, whatever the source wrapped it in. */
export const quoted = (s: string | null | undefined) => {
  const t = String(s ?? '').trim().replace(/^["'\u201C\u2018]+|["'\u201D\u2019]+$/g, '').trim();
  return t ? `\u201C${t}\u201D` : '';
};
